import { useCallback, useEffect, useRef, useState } from 'react'
import type { MediaEntry } from '../api/schema.js'
import { useModalDialog } from '../hooks/useModalDialog.js'
import styles from './Lightbox.module.css'

export interface LightboxProps {
  entries: MediaEntry[]
  initialIndex: number
  onClose: () => void
}

export function Lightbox({ entries, initialIndex, onClose }: LightboxProps) {
  const [index, setIndex] = useState(initialIndex)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const [displayedSource, setDisplayedSource] = useState(
    entries[initialIndex]?.full ?? entries[initialIndex]?.url ?? '',
  )
  const requestedSourceRef = useRef(displayedSource)
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null)
  const total = entries.length

  const step = useCallback(
    (delta: number) => {
      setIndex((current) => (current + delta + total) % total)
    },
    [total],
  )

  // 배경 스크롤 잠금·Escape·초점 관리는 스냅 모달과 같은 규칙을 공유한다.
  // cleanup은 React가 언마운트 시 항상 실행하므로 열어둔 채 부모가 사라지는
  // 경우에도 스크롤이 잠긴 채 남지 않는다.
  const dialogRef = useModalDialog(onClose)

  // 화살표 넘김은 라이트박스만의 것이다.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') step(-1)
      else if (event.key === 'ArrowRight') step(1)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [step])

  const entry = entries[index]
  if (!entry) return null
  const source = entry.full ?? entry.url
  requestedSourceRef.current = source

  const previousEntry = entries[(index - 1 + total) % total]
  const nextEntry = entries[(index + 1) % total]
  const preloadSources = new Set<string>()
  const previousSource = previousEntry?.full ?? previousEntry?.url
  const nextSource = nextEntry?.full ?? nextEntry?.url
  if (previousSource && previousSource !== source) preloadSources.add(previousSource)
  if (nextSource && nextSource !== source) preloadSources.add(nextSource)

  const isPending = source !== displayedSource && source !== failedSrc

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`웨딩 사진 ${index + 1}`}
      onClick={onClose}
    >
      <div
        className={styles.imageWrap}
        data-testid="lightbox-stage"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={(event) => {
          if (event.touches.length !== 1) {
            // 두 손가락 이상은 핀치 제스처로 간주해 스와이프를 취소한다.
            swipeStartRef.current = null
            return
          }
          const touch = event.touches[0]
          if (!touch) return
          swipeStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
          }
        }}
        onTouchEnd={(event) => {
          const start = swipeStartRef.current
          swipeStartRef.current = null
          const touch = event.changedTouches[0]
          if (!start || !touch) return

          const deltaX = touch.clientX - start.x
          const deltaY = touch.clientY - start.y
          if (Math.abs(deltaX) >= 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
            step(deltaX < 0 ? 1 : -1)
          }
        }}
        onTouchCancel={() => {
          swipeStartRef.current = null
        }}
      >
        {/* 디코딩에 실패해도 다이얼로그는 열려 있다 — 넘기면 다음 사진이 나온다. */}
        {displayedSource !== failedSrc && (
          <img
            className={styles.image}
            src={displayedSource}
            alt="웨딩 사진 크게 보기"
            decoding="async"
            draggable={false}
            onError={() => setFailedSrc(displayedSource)}
          />
        )}
        {isPending && (
          <img
            key={source}
            className={styles.bufferImage}
            data-lightbox-pending
            src={source}
            alt=""
            aria-hidden="true"
            decoding="async"
            onLoad={() => {
              if (requestedSourceRef.current === source) setDisplayedSource(source)
            }}
            onError={() => {
              if (requestedSourceRef.current === source) setFailedSrc(source)
            }}
          />
        )}
        {[...preloadSources].map((preloadSource) => (
          <img
            key={preloadSource}
            className={styles.bufferImage}
            data-lightbox-preload
            src={preloadSource}
            alt=""
            aria-hidden="true"
            decoding="async"
          />
        ))}
      </div>
      <div className={styles.counter}>
        {index + 1} / {total}
      </div>
      <button
        type="button"
        className={styles.prev}
        aria-label="이전 사진"
        onClick={(event) => {
          event.stopPropagation()
          step(-1)
        }}
      >
        <span className={`${styles.navIcon} ${styles.navIconPrev}`} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.next}
        aria-label="다음 사진"
        onClick={(event) => {
          event.stopPropagation()
          step(1)
        }}
      >
        <span className={`${styles.navIcon} ${styles.navIconNext}`} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={styles.close}
        aria-label="닫기"
        onClick={(event) => {
          event.stopPropagation()
          onClose()
        }}
      >
        ✕
      </button>
    </div>
  )
}
